import { useAuth0 } from '@auth0/auth0-react';
import { useEffect, useRef, useState } from 'react';
import { isInternalAuthMode } from '@/utils/auth-mode';
import { auth0Exchange, auth0SignUp, getSessionUser } from '@/services/auth';
import { SessionUserContext } from '@/contexts/sessionUser';
import { User } from '@/types/auth';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const LoadingScreen = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="animate-pulse text-primary-600 text-xl font-semibold">Loading...</div>
  </div>
);

/**
 * Internal auth: validate session via API, redirect to Angular login if missing.
 * No Auth0 SDK involved.
 */
// Deduplicate session checks (StrictMode fires effects twice)
let pendingSessionCheck: Promise<unknown> | null = null;

const InternalProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const hasRedirected = useRef(false);
  const [user, setUser] = useState<User | null>(null);
  const [sessionReady, setSessionReady] = useState(false);
  const [sessionChecked, setSessionChecked] = useState(false);

  useEffect(() => {
    if (sessionChecked) return;

    (async () => {
      try {
        if (!pendingSessionCheck) {
          pendingSessionCheck = getSessionUser();
        }
        const data = await pendingSessionCheck;
        pendingSessionCheck = null;
        setUser(data as User);
        setSessionReady(true);
      } catch {
        pendingSessionCheck = null;
        if (!hasRedirected.current) {
          hasRedirected.current = true;
          window.location.href = '/auth/local/login';
        }
      } finally {
        setSessionChecked(true);
      }
    })();
  }, [sessionChecked]);

  if (!sessionChecked || !sessionReady) return <LoadingScreen />;
  return (
    <SessionUserContext.Provider value={{ user }}>
      {children}
    </SessionUserContext.Provider>
  );
};

/**
 * Auth0 mode: exchange Auth0 token for Spring Session, then render children.
 */
const Auth0ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const {
    isAuthenticated,
    isLoading,
    loginWithRedirect,
    getAccessTokenSilently,
    getIdTokenClaims,
  } = useAuth0();
  const hasRedirected = useRef(false);
  const [user, setUser] = useState<User | null>(null);
  const [sessionReady, setSessionReady] = useState(false);
  const exchangeAttempted = useRef(false);

  useEffect(() => {
    if (!isAuthenticated || exchangeAttempted.current) return;
    exchangeAttempted.current = true;

    (async () => {
      try {
        const data = await getSessionUser();
        setUser(data as User);
        setSessionReady(true);
        return;
      } catch {
        // No session yet — exchange
      }

      try {
        const accessToken = await getAccessTokenSilently();
        const claims = await getIdTokenClaims();
        const idToken = claims?.__raw || '';

        let exchangeResult: { id: number; email: string };
        try {
          exchangeResult = await auth0Exchange(accessToken, idToken);
        } catch (exchangeErr: unknown) {
          const status = (exchangeErr as { response?: { status?: number } })?.response?.status;
          if (status === 401) {
            console.log('[auth] User not found, attempting Auth0 sign-up');
            await auth0SignUp(accessToken, idToken);
            exchangeResult = await auth0Exchange(accessToken, idToken);
          } else {
            throw exchangeErr;
          }
        }

        // Fetch full user after exchange
        try {
          const data = await getSessionUser();
          setUser(data as User);
        } catch {
          setUser({ id: exchangeResult.id, email: exchangeResult.email } as User);
        }
        setSessionReady(true);
      } catch (err) {
        console.error('[auth] Auth0 → Session exchange failed:', err);
        setSessionReady(true);
      }
    })();
  }, [isAuthenticated, getAccessTokenSilently, getIdTokenClaims]);

  useEffect(() => {
    if (!isLoading && !isAuthenticated && !hasRedirected.current) {
      hasRedirected.current = true;
      const returnTo = window.location.pathname + window.location.search;
      loginWithRedirect({ appState: { returnTo } });
    }
  }, [isLoading, isAuthenticated, loginWithRedirect]);

  if (isLoading || (isAuthenticated && !sessionReady)) return <LoadingScreen />;
  if (!isAuthenticated) return null;
  return (
    <SessionUserContext.Provider value={{ user }}>
      {children}
    </SessionUserContext.Provider>
  );
};

const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  if (isInternalAuthMode()) {
    return <InternalProtectedRoute>{children}</InternalProtectedRoute>;
  }
  return <Auth0ProtectedRoute>{children}</Auth0ProtectedRoute>;
};

export default ProtectedRoute;
